import axios from 'axios'
import { QueryClient, useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from '../../components/molecules/toast'
import apiClient from '../../utils/api'
import { hasManageableSubscriptionStatus } from '../../utils/subscription'
import { TUserInfo } from '../../utils/types'
import { sleep } from '../../utils/utils'

const ALREADY_SUBSCRIBED_ERROR = 'alreadySubscribed'
const CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY = 'checkout_confirmation_pending'
const CHECKOUT_CONFIRMATION_REFETCH_DELAY_MS = 1000
const CHECKOUT_CONFIRMATION_REFETCH_ATTEMPTS = 60
export const CHECKOUT_CONFIRMATION_STATUS = {
    idle: 'idle',
    confirming: 'confirming',
    needsBilling: 'needs_billing',
} as const

type TCheckoutConfirmationStatus = typeof CHECKOUT_CONFIRMATION_STATUS[keyof typeof CHECKOUT_CONFIRMATION_STATUS]

interface TSubscriptionSession {
    url: string
}

let checkoutConfirmationPoll: Promise<void> | null = null

const isCheckoutStillConfirming = (userInfo?: TUserInfo) =>
    !userInfo?.has_product_access &&
    !hasManageableSubscriptionStatus(userInfo?.subscription_status, userInfo?.is_subscribed)

const clearCheckoutConfirmationPending = (queryClient: QueryClient) => {
    queryClient.setQueryData(CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY, CHECKOUT_CONFIRMATION_STATUS.idle)
}

const pollForCheckoutConfirmation = async (queryClient: QueryClient) => {
    for (let attempt = 0; attempt < CHECKOUT_CONFIRMATION_REFETCH_ATTEMPTS; attempt++) {
        if (
            queryClient.getQueryData<TCheckoutConfirmationStatus>(CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY) !==
            CHECKOUT_CONFIRMATION_STATUS.confirming
        ) {
            return
        }
        try {
            await queryClient.refetchQueries('user_info')
            const userInfo = queryClient.getQueryData<TUserInfo>('user_info')
            if (!isCheckoutStillConfirming(userInfo)) {
                clearCheckoutConfirmationPending(queryClient)
                return
            }
        } catch {
            // Keep the confirmation state while the app retries; losing the poll should not reopen checkout.
        }
        await sleep(CHECKOUT_CONFIRMATION_REFETCH_DELAY_MS)
    }
    queryClient.setQueryData(CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY, CHECKOUT_CONFIRMATION_STATUS.needsBilling)
}

export const beginCheckoutConfirmation = (queryClient: QueryClient) => {
    queryClient.setQueryData(CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY, CHECKOUT_CONFIRMATION_STATUS.confirming)
    if (checkoutConfirmationPoll) return

    checkoutConfirmationPoll = pollForCheckoutConfirmation(queryClient).finally(() => {
        checkoutConfirmationPoll = null
    })
}

export const endCheckoutConfirmation = (queryClient: QueryClient) => {
    clearCheckoutConfirmationPending(queryClient)
}

export const useCheckoutConfirmationStatus = () => {
    const { data = CHECKOUT_CONFIRMATION_STATUS.idle } = useQuery<TCheckoutConfirmationStatus>(
        CHECKOUT_CONFIRMATION_PENDING_QUERY_KEY,
        () => CHECKOUT_CONFIRMATION_STATUS.idle,
        {
            enabled: false,
            initialData: CHECKOUT_CONFIRMATION_STATUS.idle,
        }
    )
    return data
}

const getCurrentReturnPath = () => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('subscription')
    const query = searchParams.toString()
    return `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
}

const openSubscriptionSession = ({ url }: TSubscriptionSession) => {
    window.location.assign(url)
}

const createCheckoutSession = async () => {
    try {
        const res = await apiClient.post('/subscriptions/checkout_session/', {
            return_path: getCurrentReturnPath(),
        })
        return res.data as TSubscriptionSession
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
            throw ALREADY_SUBSCRIBED_ERROR
        }
        throw 'createCheckoutSession failed'
    }
}

export const useStartCheckout = () => {
    const queryClient = useQueryClient()
    return useMutation(createCheckoutSession, {
        onSuccess: openSubscriptionSession,
        onError: (error) => {
            if (error === ALREADY_SUBSCRIBED_ERROR) {
                beginCheckoutConfirmation(queryClient)
                toast('Confirming subscription.', { type: 'blank' })
                return
            }
            toast('Could not open checkout.', { type: 'error' })
        },
    })
}

const createPortalSession = async () => {
    try {
        const res = await apiClient.post('/subscriptions/portal_session/', {
            return_path: getCurrentReturnPath(),
        })
        return res.data as TSubscriptionSession
    } catch {
        throw 'createPortalSession failed'
    }
}

export const useStartBillingPortal = () => {
    return useMutation(createPortalSession, {
        onSuccess: openSubscriptionSession,
        onError: () => {
            toast('Could not open billing.', { type: 'error' })
        },
    })
}
